import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { GET_AFFILIATES_BRANDING } from 'api/paths/constants';
import { ApiResponseType, BrandingType } from 'types';
import { RootState, Status } from 'store/types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import fetcher from 'utils/fetcher';
import { EMPTY_OBJECT } from 'constant';

type UseBrandingActionsType = {
  get: () => void;
};

type UseBrandingStateType = {
  settings: BrandingType;
  status: Status;
};

const initialState: UseBrandingStateType = {
  status: 'idle',
  settings: {
    id: 0,
    terms: '',
    actionColor: '',
    bgColor: '',
    description: '',
    backgroundImage: '',
    email: '',
    favicon: '',
    fontColor: '',
    loader: '',
    logo: '',
    loginLogo: '',
    title: '',
    socialFacebook: '',
    socialInstagram: '',
    socialTelegram: '',
    socialWhatsapp: '',
    socialX: '',
    socialYoutube: '',
  },
};

const getBranding = createAsyncThunk<ApiResponseType<BrandingType>>(
  GET_AFFILIATES_BRANDING,
  async (body, { rejectWithValue }) => {
    try {
      const response = await fetcher<BrandingType>({
        method: 'GET',
        url: GET_AFFILIATES_BRANDING,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const brandingSlice = createSlice({
  name: 'branding',
  initialState,
  reducers: EMPTY_OBJECT,
  extraReducers: (builder) =>
    builder
      .addCase(getBranding.pending.type, (state: UseBrandingStateType): void => {
        state.status = 'loading';
      })
      .addCase(getBranding.fulfilled.type, (state, action: PayloadAction<{ data: BrandingType }>): void => {
        state.settings = action.payload?.data;
        state.status = 'success';
      })
      .addCase(getBranding.rejected.type, (state: UseBrandingStateType): void => {
        state.status = 'failed';
        state.settings = initialState.settings;
      }),
});

const useBranding = (): [UseBrandingStateType, UseBrandingActionsType] => {
  const state = useAppSelector((storeState: RootState) => storeState.branding);
  const dispatch = useAppDispatch();

  const actions = {
    get: async (): Promise<void> => {
      await dispatch(getBranding());
    },
  };

  return [state, actions];
};

export { brandingSlice };

export default useBranding;
